import { BankData } from '@codefluegel/wowis-rest-typescript-client';
import { Alert, Box, Snackbar, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useKundenApi } from '../../ApiClient';
import useIbanValidator from '../../hooks/useIbanValidator';
import useMedia from '../../hooks/useMedia';
import ThemedButton from '../button/ThemedButton';

interface IBANForm {
  country: string;
  owner: string;
  iban: string;
  bic: string;
}

const IBANModal = observer(
  ({
    ibanToEdit,
    topUserId,
    open,
    onClose,
    updateTextField,
  }: {
    ibanToEdit: BankData | undefined;
    topUserId: number;
    open: boolean;
    onClose: () => void;
    updateTextField: (iban: BankData) => void;
  }): JSX.Element => {
    const { control, handleSubmit, setValue } = useForm<IBANForm>();
    const kundenApi = useKundenApi();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('Error');
    const isMobile = useMedia('md');

    useEffect(() => {
      if (ibanToEdit) {
        setValue('country', ibanToEdit.land ?? '');
        setValue('owner', ibanToEdit.kontoInhaber ?? '');
        setValue('iban', ibanToEdit.iban ?? '');
        setValue('bic', ibanToEdit.bic ?? '');
      } else {
        setValue('country', '');
        setValue('owner', '');
        setValue('iban', '');
        setValue('bic', '');
      }
    }, [ibanToEdit]);

    const handleClose = () => {
      onClose();
    };

    const saveEntry = async (values: IBANForm) => {
      setSnackbarMessage('Error');
      setSnackbarOpen(false);
      try {
        const bankData: BankData = {
          bankKontoId: ibanToEdit?.bankKontoId,
          land: values.country,
          kontoInhaber: values.owner,
          iban: values.iban.trim(),
          bic: values.bic,
        };
        if (ibanToEdit) {
          await kundenApi.updateKundenBankKonto(topUserId, bankData);
        } else {
          await kundenApi.addKundenBankKonto(topUserId, bankData);
        }
        updateTextField(bankData);
        handleClose();
      } catch (error) {
        if (error instanceof Error && typeof error.message === 'string') {
          setSnackbarMessage(error.message);
        }
        setSnackbarOpen(true);
      }
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle color="secondary">Zahlungsdaten hinzufügen</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingX: isMobile ? 0 : 10,
            }}
          >
            <Controller
              name="country"
              control={control}
              rules={{ required: 'Pflichtfeld!' }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    width: 'auto',
                    input: { color: '#58585A' },
                  }}
                  id="country"
                  label="LAND "
                  type="text"
                  variant="standard"
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <Controller
              name="owner"
              control={control}
              rules={{ required: 'Pflichtfeld!' }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    width: 'auto',
                    input: { color: '#58585A' },
                  }}
                  id="owner"
                  label="KONTOINHABER "
                  type="text"
                  inputProps={{ maxLength: 50 }}
                  variant="standard"
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <Controller
              name="iban"
              control={control}
              rules={{
                validate: {
                  required: value => {
                    if (value === '') return 'Pflichtfeld!';
                    if (!useIbanValidator(value.trim())) return '* Ungültig!';
                    return true;
                  },
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    width: 'auto',
                    input: { color: '#58585A' },
                  }}
                  id="iban"
                  label="IBAN "
                  type="text"
                  inputProps={{ maxLength: 26 }}
                  variant="standard"
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <Controller
              name="bic"
              control={control}
              rules={{ required: 'Pflichtfeld!' }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    width: 'auto',
                    input: { color: '#58585A' },
                  }}
                  id="bic"
                  label="BIC "
                  type="text"
                  inputProps={{ maxLength: 20 }}
                  variant="standard"
                  value={value}
                  error={!!error}
                  onChange={onChange}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <DialogActions
              sx={{
                justifyContent: 'space-around',
                marginTop: 4,
                marginBottom: 2,
              }}
            >
              <Button sx={{ width: 180 }} onClick={handleClose}>
                Abbrechen
              </Button>
              <Box width={180}>
                <ThemedButton
                  label="Speichern"
                  onClick={handleSubmit(saveEntry)}
                />
              </Box>
            </DialogActions>
          </Box>
          <Box marginBottom={4}>
            <Typography variant="body2" color="secondary">
              Sie werden hiemit widerruflich beauftragt, die von obengenanntem
              Zahlungsempfänger ausgefertigten und zum Einzug über mein(unser)
              Konto bestimmten Lastschriften durchzuführen. Die vom Konto
              abzubuchenden Beträge unterliegen keiner be- tragsmäßigen
              Beschränkung. Sie sind berechtigt, Lastschriften zurückzuleiten,
              insbesondere dann, wenn das Konto nicht die erforderliche Deckung
              aufweist. Teilzahlungen sind nicht zu leisten.
            </Typography>
            <br />
            <Typography variant="body2" color="secondary">
              Ich(Wir) habe(n) den Zahlungsempfänger von der Erteilung dieses
              Auftrages an Sie verständigt. Durch die Weitergabe dieser
              Mitteilung an den Zahlungsempfänger entsteht für Sie keine
              Haftung.
            </Typography>
            <br />
            <Typography variant="body2" color="secondary">
              Ich(Wir) nehme(n) zur Kenntnis, dass ein Einspruch gegen
              Belastungen, die im Rahmen dieses Auftrages erfolgen, Ihnen
              gegenüber nicht möglich ist. Einwendungen, die sich auf das der
              Lastschrift zugrunde liegende Rechtsgeschäft beziehen, sind
              zwischen mir(uns) und dem Zahlungsempfänger direkt zu regeln. Ein
              Widerruf dieses Auftrages gilt ab dem Zeitpunkt des Ein- langens
              bei der kontoführenden Stelle. Vom Widerruf werde(n) ich(wir) die
              obenangeführte Firma gleichzeitig benachrichtigen. Im übrigen
              gelten die „Allgemeinen Geschäftsbedingungen der österreichischen
              Kreditinstitute“ .
            </Typography>
          </Box>
        </DialogContent>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  },
);

export default IBANModal;
